/// <reference path="./exceljs-browser.d.ts" />
import { useEffect, useState } from 'react';
import papaparse from 'papaparse';
import { readXlsxRows } from './xlsx';
import {
  useCreateMany,
  HttpError,
  importCSVMapper,
  OnProgressParams,
  UseImportReturnType,
  UseCreateReturnType,
  UseCreateManyReturnType,
  MetaQuery,
} from '@refinedev/core';
import { sequentialPromises } from '@admin-panel/utils';

export type LKImportOptions = {
  mapData: (item: any, index?: number) => any;
  chunker: (values: any[], batchSize: number) => any[];
  resource: string;
  batchSize: number;
  paparseOptions?: papaparse.ParseConfig;
  onProgress?: (progressParams: OnProgressParams) => any;
  onFinish?: (results: { succeeded: any; errored: any }) => void;
  onError?: (error: unknown) => void;
  validateFile?: (data: unknown[][], errors: papaparse.ParseError[]) => void;
  meta?: MetaQuery;
};

export const useLkImport = ({
  resource,
  mapData = (item: any) => item as any,
  chunker,
  paparseOptions,
  batchSize = 1,
  onFinish,
  onProgress,
  onError,
  validateFile,
  meta,
}: LKImportOptions): UseImportReturnType => {
  const [processedAmount, setProcessedAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  const createMany = useCreateMany();
  const mutationResult: UseCreateReturnType | UseCreateManyReturnType = createMany;

  const handleCleanup = () => {
    setTotalAmount(0);
    setProcessedAmount(0);
    setIsLoading(false);
  };

  const handleFinish = (createdValues: any[]) => {
    const result = {
      succeeded: createdValues.filter(item => item.type === 'success') as any[],
      errored: createdValues.filter(item => item.type === 'error') as any[],
    };
    onFinish?.(result);
    setIsLoading(false);
  };

  useEffect(() => {
    onProgress?.({ totalAmount, processedAmount });
  }, [totalAmount, processedAmount]);

  const processRows = async (data: unknown[][], errors: papaparse.ParseError[]): Promise<any[]> => {
    validateFile?.(data, errors);
    const [headers = [], ...rows] = data;
    const trimmedHeaders = headers.map(header => (typeof header === 'string' ? header.trim() : header));
    const values = importCSVMapper([trimmedHeaders, ...rows], mapData);
    setTotalAmount(values.length);
    // Create Chunks`
    const chunks = chunker(values, batchSize);
    // Create Chunk Processor Functions
    const chunkedFns = chunks.map(chunkedValues => {
      const fn = async () => {
        const response = await createMany.mutateAsync({
          resource,
          meta,
          values: chunkedValues,
          successNotification: false,
          errorNotification: false,
        });

        return {
          response,
          value: chunkedValues,
          currentBatchLength: chunkedValues.length,
        };
      };
      return fn;
    });
    // Sequentially run chunked functions and process resolves/rejects
    return sequentialPromises(
      chunkedFns,
      ({ response, currentBatchLength, value }) => {
        setProcessedAmount(currentAmount => {
          return currentAmount + currentBatchLength;
        });

        return {
          response: response.data, // List<SKUUploadResponseEntry>
          type: 'success',
          request: value, // List<SKUUploadEntry>
        };
      },
      (error: HttpError, index) => {
        setProcessedAmount(currentAmount => {
          return currentAmount + chunks[index].length;
        });
        console.log('Error Processing', error);
        return {
          response: [error],
          type: 'error',
          request: chunks[index],
        };
      },
    );
  };

  const handleChange = ({ file }: any) => {
    handleCleanup();
    return new Promise<any[]>(resolve => {
      setIsLoading(true);
      const fail = (error: unknown) => {
        console.error('Error reading import file:', error);
        onError?.(error);
        handleCleanup();
        resolve([]);
      };
      if (typeof file?.name === 'string' && file.name.toLowerCase().endsWith('.xlsx')) {
        readXlsxRows(file)
          .then(rows => processRows(rows, []))
          .then(resolve)
          .catch(fail);
        return;
      }
      papaparse.parse(file as any, {
        ...paparseOptions,
        complete: async ({ data, errors }: papaparse.ParseResult<unknown[]>) => {
          try {
            resolve(await processRows(data as unknown[][], errors));
          } catch (error) {
            fail(error);
          }
        },
        error: fail,
      });
    }).then(createdValues => {
      handleFinish(createdValues);
      return createdValues;
    });
  };

  return {
    inputProps: {
      type: 'file',
      accept: '.csv,.xlsx',
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
          handleChange({ file: event.target.files[0] });
          event.target.value = '';
        }
      },
    },
    mutationResult,
    isLoading,
    handleChange,
  };
};
